import { Select } from '@sixfold/common-ui';
import { BackofficeScope } from '@sixfold/session-interface';
import React, { useState } from 'react';

import { getRelationshipTypeLabel, RelationshipSnippet } from './relationship_snippet';
import { CompaniesDropdownContainer, CompanySearchResult } from '../../company/containers/companies_dropdown_container';
import { CompanyGroupLinkConfirmModal } from '../../company_group/components/company_group_link_confirm_modal';
import { Button } from '../../components/button';
import { useHasScopes, useIsAdmin } from '../../lib/authorization';
import { CompanyRelationshipType } from '../../lib/graphql';

export interface RelationshipViewProps {
  routeProps: {
    goBack: () => void;
    company_id: string;
  };
  isNewRelationship: boolean;
  createCompanyRelationship: (input: {
    sourceCompanyId: string;
    targetCompanyId: string;
    relationshipType: CompanyRelationshipType;
  }) => Promise<void>;
}

const CompanyRelationshipTypeWithoutReferrer = Object.keys(CompanyRelationshipType).filter(
  (type) => type !== 'REFERRER',
);

export const RelationshipView: React.FunctionComponent<RelationshipViewProps> = ({
  routeProps,
  isNewRelationship,
  createCompanyRelationship,
}) => {
  const isNotAdmin = !useIsAdmin();
  const hasCompanyRelationshipPermission = useHasScopes([BackofficeScope.companyWriteNetworkRelations]);
  const isPrivilegedUser = !isNotAdmin || hasCompanyRelationshipPermission;
  const [sourceCompany, setSourceCompany] = useState<CompanySearchResult | null>(null);
  const [targetCompany, setTargetCompany] = useState<CompanySearchResult | null>(null);
  const [relationshipType, setRelationshipType] = useState<CompanyRelationshipType>(CompanyRelationshipType.CARRIER);
  const [isCompanyGroupConfirmOpen, setIsCompanyGroupConfirmOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  if (!isNewRelationship) {
    return <div>You cannot update existing relationships</div>;
  }

  const create = async () => {
    if (sourceCompany === null || targetCompany === null) {
      return;
    }

    setIsSaving(true);
    try {
      await createCompanyRelationship({
        sourceCompanyId: sourceCompany.company_id,
        targetCompanyId: targetCompany.company_id,
        relationshipType,
      });
    } finally {
      setIsSaving(false);
      setIsCompanyGroupConfirmOpen(false);
    }

    routeProps.goBack();
  };

  return (
    <React.Fragment>
      <RelationshipSnippet
        sourceCompany={sourceCompany}
        targetCompany={targetCompany}
        relationshipType={relationshipType}
      />
      <Button
        style={{ margin: '0 4px' }}
        size="xsmall"
        onClick={() => {
          setSourceCompany(targetCompany);
          setTargetCompany(sourceCompany);
        }}>
        Switch companies
      </Button>
      <form style={{ marginTop: 15 }} onSubmit={(e) => e.preventDefault()}>
        <div className="field">
          <label>Source company</label>
          <CompaniesDropdownContainer
            inputProps={{
              disabled: !isPrivilegedUser,
              placeholder: 'Source company',
            }}
            selectedCompany={sourceCompany}
            onSelectCompany={setSourceCompany}
          />
        </div>
        <div className="field">
          <label>Relationship Type</label>
          <Select
            isFilterable
            options={CompanyRelationshipTypeWithoutReferrer.map((type) => ({
              value: type,
              label: getRelationshipTypeLabel(type),
            }))}
            disabled={!isPrivilegedUser}
            value={{
              value: relationshipType as string,
              label: getRelationshipTypeLabel(relationshipType),
            }}
            name="relationship_type"
            placeholder="Relationship type"
            onChange={(option) => {
              const value = option?.value;
              if (value !== undefined) {
                setRelationshipType(value as CompanyRelationshipType);
              }
            }}
          />
        </div>
        <div className="field">
          <label>Target company</label>
          <CompaniesDropdownContainer
            inputProps={{
              disabled: !isPrivilegedUser,
              placeholder: 'Target company',
            }}
            selectedCompany={targetCompany}
            onSelectCompany={setTargetCompany}
          />
        </div>
        <Button
          type="submit"
          kind="primary"
          disabled={!isPrivilegedUser || isSaving}
          onClick={async () => {
            if (sourceCompany === null || targetCompany === null) {
              return;
            }

            if (relationshipType === CompanyRelationshipType.COMPANY_GROUP) {
              setIsCompanyGroupConfirmOpen(true);
              return;
            }

            await create();
          }}>
          Create
        </Button>
        <Button type="button" onClick={() => routeProps.goBack()}>
          Cancel
        </Button>
      </form>
      <CompanyGroupLinkConfirmModal
        open={isCompanyGroupConfirmOpen}
        memberName={sourceCompany?.company_name ?? sourceCompany?.company_id ?? ''}
        hubName={targetCompany?.company_name ?? targetCompany?.company_id ?? ''}
        isSaving={isSaving}
        onCancel={() => setIsCompanyGroupConfirmOpen(false)}
        onConfirm={create}
      />
    </React.Fragment>
  );
};
